import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiEdit3 } from "react-icons/fi";

import MainContainer from "../../components/MainContainer";
import DropIcon from "../../components/DropIcon/DropIcon";
import TopUpModal from "../../components/TopUpModal/TopUpModal";
import UsernameModal from "../../components/UsernameModal/UsernameModal";

import { selectUser } from "../../store/slices/user/slice";
import { selectCreditsHistory } from "../../store/slices/subscription/slice";

import { formatDate, priceFormatter } from "../../utils/utils";
import SeoHead from "../../components/Seo/SeoHead.js";
import {
  buildBreadcrumbSchema,
  buildPageTitle,
  buildWebsiteSchema,
} from "../../seo/siteSeo.js";

import {
  ActivityTable,
  Amount,
  BalanceCard,
  BalanceIconWrap,
  BalanceLabel,
  BalanceMeta,
  BalanceValue,
  BalanceValueRow,
  DangerCard,
  DangerChevron,
  DangerExpansion,
  DangerExpansionInner,
  DangerHeader,
  DangerText,
  DangerTitle,
  DeleteButton,
  EditUsernameButton,
  Email,
  EmptyHistory,
  PageWrap,
  ProfileInfo,
  ProfileRow,
  Section,
  SectionTitle,
  SkeletonBar,
  StatusPill,
  TD,
  TH,
  TopUpButton,
  Username,
  UsernameRow,
  VerifiedDot,
} from "./SubscriptionPage.styles";
import { getSubscriptionsCreditsHistory } from "../../store/slices/subscription/asyncThunks.js";
import { selectLoggedIn } from "../../store/slices/auth/slice.js";

/* Maps backend `type` strings to short, friendly labels for the activity table.
   Anything we don't have a custom label for falls through to the raw type so
   we never show empty cells. */
const TYPE_LABELS = {
  TRIAL_CREDIT: "Trial Credit",
  TRIAL: "Trial Credit",
  RECHARGE: "Top Up",
  RECHARGE_CREDITS: "Top Up",
  SUBSCRIPTION: "Subscription",
  SUBSCRIPTION_RENEWAL: "Subscription Renewal",
  MODEL_USAGE: "Generation",
  AFFILIATE_SUBSCRIPTION: "Affiliate Bonus",
  AFFILIATE: "Affiliate",
  REFUND: "Refund",
};

const labelForType = (item) => {
  if (item?.planName) return item.planName;
  return TYPE_LABELS[item?.type] || item?.type || "Activity";
};

/* Number of skeleton placeholder rows shown while the activity feed
   is loading. 6 keeps the table tall enough to read as "real content"
   without pushing the Danger zone too far down on small screens. */
const SKELETON_ROW_COUNT = 6;

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const loggedIn = useSelector(selectLoggedIn);

  const creditsHistory = useSelector(selectCreditsHistory);
  const creditsHistoryData = creditsHistory?.data;
  /* The slice now starts in `loading: true` so the skeleton renders
     from the first paint without a "No activity yet" flash. The flag
     flips to false only when the thunk fulfils or rejects. */
  const isFetchingHistory = !!creditsHistory?.loading;
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const [isUsernameModalOpen, setIsUsernameModalOpen] = useState(false);
  const [isDangerOpen, setIsDangerOpen] = useState(false);

  const handleDeleteAccount = () => {
    navigate("/account/delete");
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  };

  const recentActivities = (creditsHistoryData || []).slice(0, 8);
  /* Skeleton renders for any logged-in user as long as the slice
     reports loading. Logged-out users get the empty state directly. */
  const showSkeleton = isFetchingHistory;

  /* Pre-load credits history so the navbar can decide between routing
       to /subscription (has activity/balance) vs. opening the TopUp modal
       directly (brand-new account with nothing to show). */
  useEffect(() => {
    if (loggedIn) {
      dispatch(getSubscriptionsCreditsHistory(1))
        .unwrap()
        .catch((err) => console.log(err.message));
    }
  }, [loggedIn, dispatch]);

  return (
    <>
      <SeoHead
        title={buildPageTitle("Account & Subscription")}
        description="Manage your Fapify account, balance and subscription."
        canonicalPath="/subscription"
        jsonLd={[
          buildWebsiteSchema(),
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Subscription", path: "/subscription" },
          ]),
        ]}
      />

      <MainContainer hideOverflowX>
        <PageWrap>
          <ProfileRow>
            <ProfileInfo>
              <UsernameRow>
                <Username>{user?.username || "Account"}</Username>
                <EditUsernameButton
                  onClick={() => setIsUsernameModalOpen(true)}
                  aria-label="Edit username"
                  type="button"
                >
                  <FiEdit3 size={13} />
                </EditUsernameButton>
              </UsernameRow>
              {user?.email ? (
                <Email>
                  {user.email}
                  {user?.emailVerified ? <VerifiedDot /> : null}
                </Email>
              ) : null}
            </ProfileInfo>
          </ProfileRow>

          <BalanceCard>
            <BalanceIconWrap aria-hidden="true">
              <DropIcon size={44} />
            </BalanceIconWrap>
            <BalanceMeta>
              <BalanceLabel>Drops</BalanceLabel>
              <BalanceValueRow>
                <BalanceValue>
                  {priceFormatter(user?.credits ?? 0)}
                </BalanceValue>
              </BalanceValueRow>
            </BalanceMeta>
            <TopUpButton onClick={() => setIsTopUpOpen(true)} type="button">
              Top Up Balance
            </TopUpButton>
          </BalanceCard>

          <Section>
            <SectionTitle>Recent activities</SectionTitle>
            <ActivityTable>
              <TH>Type</TH>
              <TH>Balance change</TH>
              <TH>Status</TH>
              <TH>Date</TH>
              {showSkeleton ? (
                /* Pending state — render N rows of shimmer bars in
                   the same 4-column shape as real records. Per-row
                   widths are randomised slightly via a deterministic
                   index-based pattern so the placeholder rows don't
                   look like xerox copies of each other while still
                   not jittering between renders. */
                Array.from({ length: SKELETON_ROW_COUNT }).map((_, idx) => {
                  const isLast = idx === SKELETON_ROW_COUNT - 1;
                  const typeWidths = ["72%", "58%", "66%", "82%", "60%", "70%"];
                  const amountWidths = [
                    "48%",
                    "60%",
                    "44%",
                    "54%",
                    "62%",
                    "50%",
                  ];
                  const dateWidths = ["80%", "76%", "84%", "78%", "82%", "76%"];
                  return (
                    <React.Fragment key={`skeleton-${idx}`}>
                      <TD $row={isLast ? "last" : undefined}>
                        <SkeletonBar $w={typeWidths[idx % typeWidths.length]} />
                      </TD>
                      <TD $row={isLast ? "last" : undefined}>
                        <SkeletonBar
                          $w={amountWidths[idx % amountWidths.length]}
                        />
                      </TD>
                      <TD $row={isLast ? "last" : undefined}>
                        <SkeletonBar $variant="pill" $w="78px" />
                      </TD>
                      <TD $row={isLast ? "last" : undefined}>
                        <SkeletonBar $w={dateWidths[idx % dateWidths.length]} />
                      </TD>
                    </React.Fragment>
                  );
                })
              ) : recentActivities.length === 0 ? (
                <EmptyHistory>No activity yet.</EmptyHistory>
              ) : (
                recentActivities.map((item, idx) => {
                  const isLast = idx === recentActivities.length - 1;
                  const credits = item?.numOfCredits ?? 0;
                  return (
                    <React.Fragment key={item?._id || idx}>
                      <TD $row={isLast ? "last" : undefined}>
                        {labelForType(item)}
                      </TD>
                      <TD $row={isLast ? "last" : undefined}>
                        <Amount $positive={credits > 0}>
                          {credits > 0 ? "+" : ""}
                          {priceFormatter(credits)}
                        </Amount>
                      </TD>
                      <TD $row={isLast ? "last" : undefined}>
                        <StatusPill $variant="completed">Completed</StatusPill>
                      </TD>
                      <TD $row={isLast ? "last" : undefined}>
                        {item?.createdAt ? formatDate(item.createdAt) : "—"}
                      </TD>
                    </React.Fragment>
                  );
                })
              )}
            </ActivityTable>
          </Section>

          <DangerCard>
            <DangerHeader
              type="button"
              aria-expanded={isDangerOpen}
              aria-controls="danger-zone-panel"
              onClick={() => setIsDangerOpen((open) => !open)}
            >
              <DangerTitle>Delete account</DangerTitle>
              <DangerChevron $open={isDangerOpen} aria-hidden="true">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M6 9l6 6 6-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </DangerChevron>
            </DangerHeader>
            <DangerExpansion $open={isDangerOpen} id="danger-zone-panel">
              <div>
                <DangerExpansionInner>
                  <DangerText>
                    Permanently delete your account and all associated data.
                    This cannot be undone.
                  </DangerText>
                  <DeleteButton onClick={handleDeleteAccount} type="button">
                    Delete Account
                  </DeleteButton>
                </DangerExpansionInner>
              </div>
            </DangerExpansion>
          </DangerCard>
        </PageWrap>
      </MainContainer>

      <TopUpModal isOpen={isTopUpOpen} onClose={() => setIsTopUpOpen(false)} />
      <UsernameModal
        isOpen={isUsernameModalOpen}
        onClose={() => setIsUsernameModalOpen(false)}
      />
    </>
  );
};

export default SubscriptionPage;
